import ExpandCollapse from "common/Animations/ExpandCollapse";
import OrderSectionErrorsSummary from "features/trades/common/OrderSectionErrorsSummary";
import Space from "antd/es/space";
import Order from "features/tradingTerminal/Orders/Order";
import React, { useContext } from "react";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import styled, { useTheme } from "styled-components";
import Row from "antd/es/row";
import Col from "antd/es/col";
import { NEW_LOOK_REMOVE_COLUMN } from "features/tradingTerminal/Orders/Order";
import { StyledCardSorters } from "features/tradingTerminal/common/styles";
import { OrdersTableProps } from "@shared/features/tradingTerminal/common/types";
import { LanguageNameSpaces, OrderTypes } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

const OrdersTable = ({
  orderType,
  orders,
  useOrders,
  ordersErrors,
  resolveIssues,
  updateOrder,
  removeOrder,
  baselineEntryPrice,
  title,
  readonly,
  spacing,
  ignoreRatios,
  headerAction,
}: OrdersTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const theme = useTheme();
  const { amount, isSignal } = useContext(TradingTerminalContext)!;
  const isEntry = orderType === OrderTypes.ENTRY;
  const isSignalEntry = isSignal && isEntry;

  const headerName = {
    price: t("table_headers.price"),
    ratio: t("table_headers.ratio"),
    amount: t("table_headers.amount"),
    distance: t("table_headers.distance"),
    priceDistance: `${t("table_headers.price")} (${t(
      "table_headers.distance"
    )})`,
    amountRatio: `${t("table_headers.amount")} (${t("table_headers.ratio")})`,
  };
  const orderTypeToColumnNames = {
    [OrderTypes.ENTRY]: [headerName.price, headerName.ratio, headerName.amount],
    [OrderTypes.TAKE_PROFIT]: [
      headerName.price,
      headerName.ratio,
      headerName.distance,
    ],
    [OrderTypes.STOP]: [
      headerName.price,
      headerName.ratio,
      headerName.distance,
    ],
  };
  const orderTypeToSummaryColumnNames = {
    [OrderTypes.ENTRY]: [headerName.priceDistance, headerName.amountRatio],
    [OrderTypes.TAKE_PROFIT]: [headerName.priceDistance, headerName.ratio],
    [OrderTypes.STOP]: [headerName.priceDistance, headerName.ratio],
  };

  const getColumnNamesAndSpans = () => {
    let names = [
      ...(readonly ? orderTypeToSummaryColumnNames : orderTypeToColumnNames)[
        orderType
      ],
    ];
    if (isSignalEntry) {
      if (readonly) names[1] = headerName.ratio;
      else names.splice(2, 1);
    }
    if (ignoreRatios)
      names = names.map((name) => (name === headerName.ratio ? " " : name));
    const numNames = names.filter((name) => name !== " ").length;
    let spans = [10, 6, 8];
    if (numNames === 1) spans = [24, 0, 0];
    if (numNames === 2) {
      spans = [14, 10, 0];
      const emptyIndex = names.findIndex((name) => name === " ");
      if (emptyIndex === 1) spans = [14, 0, 10];
    }
    return [names, spans] as const;
  };

  const [columnNames, columnSpans] = getColumnNamesAndSpans();

  return (
    <>
      {useOrders && (
        <ExpandCollapse
          expanded={Object.keys(ordersErrors?.section || {}).length > 0}
        >
          <OrderSectionErrorsSummary
            errors={ordersErrors?.section || {}}
            orderType={orderType}
            resolveIssues={resolveIssues}
          />
        </ExpandCollapse>
      )}
      {title}
      {theme.isNewLook && !readonly ? (
        // One header row on the order rows' own grid: "#" over the numbers,
        // labels flush with their fields, and the add button over the remove
        // buttons.
        <NewLookHeader>
          {columnNames.map((name, index) => (
            <Col span={columnSpans[index]} key={index}>
              <HeaderCell>
                {index === 0 && <HeaderIndex>#</HeaderIndex>}
                {name.trim()}
              </HeaderCell>
            </Col>
          ))}
          {headerAction && <HeaderAction>{headerAction}</HeaderAction>}
        </NewLookHeader>
      ) : (
        <StyledCardSorters
          $editable={!readonly}
          columnNames={columnNames}
          columnSpans={columnSpans}
          sorterStyle={
            theme.isNewLook
              ? NEW_LOOK_SORTER_STYLE(theme.colors.secondaryText)
              : { color: theme.colors.primaryText }
          }
        />
      )}
      <Space size={spacing} orientation="vertical" style={{ width: "100%" }}>
        {orders.map((order, index) => (
          <Order
            order={order}
            index={index}
            key={index}
            updateOrder={updateOrder}
            orderType={orderType}
            amount={Number(amount)}
            removeOrder={removeOrder}
            orderSectionErrors={ordersErrors}
            baselineEntryPrice={baselineEntryPrice}
            readonly={readonly}
            columnSpans={columnSpans}
            ignoreRatios={ignoreRatios}
          />
        ))}
      </Space>
    </>
  );
};

export default OrdersTable;

const NEW_LOOK_SORTER_STYLE = (color: string) => ({
  color,
  fontSize: 12,
  fontWeight: 500,
});

const NewLookHeader = styled(Row)`
  position: relative;
  align-items: center;
  min-height: 28px;
  margin-bottom: 4px;
  padding-right: ${NEW_LOOK_REMOVE_COLUMN}px;
`;
// Column headers in the micro role, as in every table.
const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
// Same box as the order number badge in each row (18px + 8px gap).
const HeaderIndex = styled.span`
  width: 18px;
  margin-right: 8px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;
const HeaderAction = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
`;
